import {useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {useQuery, type UseQueryResult} from '@tanstack/react-query'

import Toast from 'components/Basic/Toast'
import {AIUsageSummaryAPIResponse, AIUsageSummary} from 'interfaces'
import {AIUsageService} from 'services/api'

export const AI_USAGE_SUMMARY_QUERY_KEY = 'getAIUsageSummary'

export type UseAIUsageSummaryQueryProps = {
  enabled?: boolean
  /**
   * When true (default), shows the generic summary fetch error toast on failure.
   * Set false to handle errors in the caller (e.g. custom UI or messaging).
   */
  showErrorToast?: boolean
}

type UseAIUsageSummaryQueryResult = Pick<
  UseQueryResult<AIUsageSummaryAPIResponse, Error>,
  'isLoading' | 'isSuccess' | 'isError' | 'error' | 'refetch'
> & {
  data?: AIUsageSummary
}

export const useAIUsageSummaryQuery = ({
  enabled = true,
  showErrorToast = true,
}: UseAIUsageSummaryQueryProps = {}): UseAIUsageSummaryQueryResult => {
  const {t} = useTranslation(['aiUsage'])
  const {data, isLoading, isSuccess, isError, error, refetch} = useQuery<
    AIUsageSummaryAPIResponse,
    Error
  >({
    queryKey: [AI_USAGE_SUMMARY_QUERY_KEY],
    queryFn: () => AIUsageService.getAIUsageSummary(),
    enabled,
  })

  useEffect(() => {
    if (isError && showErrorToast) {
      Toast({
        message: t('aiUsage:AI_USAGE_TOAST_SUMMARY_FETCH_ERROR'),
        type: 'error',
        customId: 'ai-usage-summary-fetch-error',
      })
    }
  }, [isError, showErrorToast, t])

  return {
    data: data?.data,
    isLoading,
    isSuccess,
    isError,
    error,
    refetch,
  }
}
